import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type {
  TypeGetDashboardDeliveryTrendParams,
  TypeGetDashboardDeliveryTrendResponse,
} from '~/types/delivery-agency.types';

/**
 * [배송 현황 대시보드] 배송 추이 조회 API
 */
const fetchDashboardDeliveryTrend = async (
  params: TypeGetDashboardDeliveryTrendParams
): Promise<TypeGetDashboardDeliveryTrendResponse> => {
  const { data } = await axiosInstance.get<TypeGetDashboardDeliveryTrendResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_DASHBOARD_DELIVERY_TREND,
    { params }
  );
  return data;
};

type UseGetDashboardDeliveryTrendQueryOptions = {
  params: TypeGetDashboardDeliveryTrendParams;
  enabled?: boolean;
};

/**
 * [배송 현황 대시보드] 배송 추이 조회 Query Hook
 */
export const useGetDashboardDeliveryTrendQuery = ({
  params,
  enabled = true,
}: UseGetDashboardDeliveryTrendQueryOptions) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.dashboardDeliveryTrend(params),
    queryFn: () => fetchDashboardDeliveryTrend(params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
  });
};

registerMockAPI<TypeGetDashboardDeliveryTrendResponse>({
  method: 'get',
  endpoint: '/api-delivery-agency/dashboard/delivery-trend',
  label: '배송 현황/추이',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          dailyTrends: [
            { deliveryDate: '2026-06-30', nightAcceptedCount: 40, dawnAcceptedCount: 120, dayAcceptedCount: 90, delayedRate: 1.2, failedRate: 0.4 },
            { deliveryDate: '2026-07-01', nightAcceptedCount: 55, dawnAcceptedCount: 110, dayAcceptedCount: 80, delayedRate: 2.1, failedRate: 0.6 },
            { deliveryDate: '2026-07-02', nightAcceptedCount: 60, dawnAcceptedCount: 130, dayAcceptedCount: 95, delayedRate: 1.5, failedRate: 0.3 },
            { deliveryDate: '2026-07-03', nightAcceptedCount: 48, dawnAcceptedCount: 125, dayAcceptedCount: 88, delayedRate: 3.0, failedRate: 0.9 },
            { deliveryDate: '2026-07-04', nightAcceptedCount: 70, dawnAcceptedCount: 140, dayAcceptedCount: 100, delayedRate: 1.8, failedRate: 0.5 },
          ],
        },
      },
    },
    {
      name: '데이터 없음',
      response: { data: { dailyTrends: [] } },
    },
  ],
});
